import { z } from 'zod'
import { rbacSlugSchema } from './rbac'

/**
 * Temporary grants — time-boxed access, served by the `/api/rbac/grants` routes.
 *
 * WorkOS has no expiring grant of any kind (neither stacked RBAC roles nor FGA role
 * assignments carry a TTL), so the expiry is app-owned: the grant row records it, a scheduled
 * QStash callback enforces it. Two shapes of grant share this contract:
 *
 * - org-wide: a role stacked onto the WorkOS organization membership (`resourceType`/`resourceId`
 *   absent). Requires Multiple Roles enabled on the WorkOS environment.
 * - resource-scoped: a WorkOS FGA role assignment on one resource, e.g. one account
 *   (`resourceType`/`resourceId` present; `resourceId` is our external id, such as accounts.id).
 */

export const grantKindSchema = z.enum(['role', 'impersonation'])

export const temporaryGrantSchema = z.object({
  id: z.string(),
  /** 'role' is live; 'impersonation' is reserved groundwork — creation answers 400 today. */
  kind: grantKindSchema,
  /** WorkOS membership (`om_…`) holding the granted role — joins to the members list. */
  membershipId: z.string(),
  roleSlug: z.string(),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  reason: z.string().nullable(),
  /** Local users.id of the granter — the audit trail's "who". */
  grantedBy: z.string(),
  expiresAt: z.string(),
  /** Null while in force. Null past expiresAt means the scheduled revocation hasn't landed yet. */
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
})

/** Ceiling on how far out a grant may expire. Longer than this isn't temporary — make it a role. */
export const GRANT_MAX_DAYS = 90

export const createGrantSchema = z
  .object({
    membershipId: z.string().min(1),
    roleSlug: rbacSlugSchema,
    kind: grantKindSchema.default('role'),
    expiresAt: z.iso.datetime({ offset: true }),
    reason: z.string().min(1).max(500).optional(),
    resourceType: rbacSlugSchema.optional(),
    resourceId: z.string().min(1).max(256).optional(),
  })
  .refine((g) => (g.resourceType === undefined) === (g.resourceId === undefined), {
    message:
      'resourceType and resourceId come together — both for a resource-scoped grant, neither for org-wide',
    path: ['resourceId'],
  })
  .refine((g) => g.kind !== 'impersonation' || g.reason !== undefined, {
    message: 'impersonation grants require a reason',
    path: ['reason'],
  })
  .refine((g) => new Date(g.expiresAt).getTime() > Date.now(), {
    message: 'expiresAt must be in the future',
    path: ['expiresAt'],
  })
  .refine((g) => new Date(g.expiresAt).getTime() <= Date.now() + GRANT_MAX_DAYS * 86_400_000, {
    message: `expiresAt is more than ${GRANT_MAX_DAYS} days out — that isn't temporary; assign the role instead`,
    path: ['expiresAt'],
  })

export const listGrantsResponseSchema = z.object({
  grants: z.array(temporaryGrantSchema),
})

export type GrantKind = z.infer<typeof grantKindSchema>
export type TemporaryGrant = z.infer<typeof temporaryGrantSchema>
export type CreateGrant = z.infer<typeof createGrantSchema>
export type ListGrantsResponse = z.infer<typeof listGrantsResponseSchema>
